import { replaceMulti } from '@go-shared/lib/functional';

export const ROUTES = {
  HOME_ROUTE(): string {
    return '/';
  },
  LOGIN_ROUTE(): string {
    return '/login';
  },
  NOTIFICATIONS_ROUTE(): string {
    return '/alerts';
  },
  MAINTENANCE_ROUTE(): string {
    return '/under-maintenance';
  },
  CHECK_VERSION_ROUTE(): string {
    return '/check-version';
  },
  HELP_ROUTE(): string {
    return '/help';
  },
  PROPERTIES_ROUTE(): string {
    return '/properties';
  },
  BRAND_SETTINGS_ROUTE(): string {
    return '/config';
  },
  SUPER_ADMIN_SETTINGS_ROUTE(): string {
    return '/admin';
  },
  ITINERARIES_ROUTE(): string {
    return '/property/:property/itinerary';
  },
  TEMPLATES_ROUTE(): string {
    return '/property/:property/templates';
  },
  GROUPS_ROUTE(): string {
    return '/property/:property/groups';
  },
  GROUP_PARTIES_ROUTE(): string {
    return '/property/:property/groups/parties';
  },
  TRANSLATIONS_ROUTE(type: TRANSLATIONS_SUBNAV_TYPE): string {
    return `/property/:property/translations/${type}`;
  },
  LIBRARIES_ROUTE(type: 'events' | 'venues' | 'staff' | 'content' | 'plans' | 'feed'): string {
    return `/property/:property/library/${type}`;
  },
  ASSETS_ROUTE(type: 'images'): string {
    return `/property/:property/assets/${type}`;
  },
  PLANNER_ROUTE(): string {
    return '/property/:property/planner/itinerary/:sailing/:sailingDay';
  },
  FEED_ROUTE(): string {
    return '/property/:property/feed/itinerary/:sailing/:sailingDay';
  },
  TEMPLATE_FEED_ROUTE(): string {
    return '/property/:property/feed/template/:template';
  },
  TEMPLATE_PLANNER_ROUTE(): string {
    return '/property/:property/planner/template/:template';
  },
  ITINERARY_MANAGEMENT_ROUTE(): string {
    return '/property/:property/itinerary-management';
  },
  PROPERTY_SETTINGS_ROUTE(): string {
    return '/property/:property/config';
  },
  USER_MANAGEMENT_ROUTE(): string {
    return '/tools/user-management';
  },
  FEED_BLUEPRINT_ROUTE(): string {
    return '/property/:property/feed-blueprint-editor';
  },
};

export function resolveHomePath(): string {
  return ROUTES.HOME_ROUTE();
}

export function resolveLoginPath(): string {
  return ROUTES.LOGIN_ROUTE();
}

export function resolveNotificationsPath(): string {
  return ROUTES.NOTIFICATIONS_ROUTE();
}

export function resolveMaintenancePath(): string {
  return ROUTES.MAINTENANCE_ROUTE();
}

export function resolveCheckVersionPath(): string {
  return ROUTES.CHECK_VERSION_ROUTE();
}

export function resolveHelpPath(): string {
  return ROUTES.HELP_ROUTE();
}

export function resolvePropertiesPath(): string {
  return ROUTES.PROPERTIES_ROUTE();
}

export function resolveBrandSettingsPath(): string {
  return ROUTES.BRAND_SETTINGS_ROUTE();
}

export function resolveItinerariesPath(propertyId: string): string {
  return ROUTES.ITINERARIES_ROUTE().replace(':property', propertyId);
}

export function resolveTemplatesPath(propertyId: string): string {
  return ROUTES.TEMPLATES_ROUTE().replace(':property', propertyId);
}

export function resolveGroupsPath(propertyId: string): string {
  return ROUTES.GROUPS_ROUTE().replace(':property', propertyId);
}

export function resolveSuperAdminSettingsPath(): string {
  return ROUTES.SUPER_ADMIN_SETTINGS_ROUTE();
}

export function resolveGroupPartiesPath(propertyId: string): string {
  return ROUTES.GROUP_PARTIES_ROUTE().replace(':property', propertyId);
}

export type TRANSLATIONS_SUBNAV_TYPE =
  | 'events'
  | 'venues'
  | 'content'
  | 'segments'
  | 'levels'
  | 'attributes'
  | 'venueTypes'
  | 'mealPeriods'
  | 'dayThemes'
  | 'highlights'
  | 'miscellaneous'
  | 'commonTexts';

export const TRANSLATIONS_SUBNAV_LIBRARY: TRANSLATIONS_SUBNAV_TYPE[] = [
  'events',
  'venues',
  'content',
];

export const TRANSLATIONS_SUBNAV_OTHERS: TRANSLATIONS_SUBNAV_TYPE[] = [
  'segments',
  'levels',
  'attributes',
  'venueTypes',
  'mealPeriods',
  'dayThemes',
  'highlights',
  'miscellaneous',
  'commonTexts',
];

export const TRANSLATIONS_SUBNAV_ARRAY = [
  ...TRANSLATIONS_SUBNAV_LIBRARY,
  ...TRANSLATIONS_SUBNAV_OTHERS,
];

export function resolveTranslationsPath(
  propertyId: string,
  type: TRANSLATIONS_SUBNAV_TYPE
): string {
  return ROUTES.TRANSLATIONS_ROUTE(type).replace(':property', propertyId);
}

export function resolveLibrariesPath(
  propertyId: string,
  type: 'events' | 'venues' | 'staff' | 'content' | 'plans' | 'feed'
): string {
  return ROUTES.LIBRARIES_ROUTE(type).replace(':property', propertyId);
}

export function resolveAssetsPath(propertyId: string, type: 'images'): string {
  return ROUTES.ASSETS_ROUTE(type).replace(':property', propertyId);
}

export function resolvePlannerPath(
  propertyId: string,
  sailingId: string,
  sailingDayId: string
): string {
  return replaceMulti(
    ROUTES.PLANNER_ROUTE(),
    [':property', propertyId],
    [':sailing', sailingId],
    [':sailingDay', sailingDayId]
  );
}

export function resolveFeedPath(
  propertyId: string,
  sailingId: string,
  sailingDayId: string
): string {
  return replaceMulti(
    ROUTES.FEED_ROUTE(),
    [':property', propertyId],
    [':sailing', sailingId],
    [':sailingDay', sailingDayId]
  );
}

export function resolveTemplateFeedPath(propertyId: string, templateId: string): string {
  return replaceMulti(
    ROUTES.TEMPLATE_FEED_ROUTE(),
    [':property', propertyId],
    [':template', templateId]
  );
}

export function resolveTemplatePlannerPath(propertyId: string, templateId: string): string {
  return replaceMulti(
    ROUTES.TEMPLATE_PLANNER_ROUTE(),
    [':property', propertyId],
    [':template', templateId]
  );
}

export function resolveItineraryManagementPath(propertyId: string): string {
  return ROUTES.ITINERARY_MANAGEMENT_ROUTE().replace(':property', propertyId);
}

export function resolvePropertySettingsPath(propertyId: string): string {
  return ROUTES.PROPERTY_SETTINGS_ROUTE().replace(':property', propertyId);
}

export function resolveUserManagementPath(): string {
  return ROUTES.USER_MANAGEMENT_ROUTE();
}

export function resolveFeedBlueprint(propertyId: string): string {
  return ROUTES.FEED_BLUEPRINT_ROUTE().replace(':property', propertyId);
}
